import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartConfig }                         from '../chart';
import { ChartHelper }                         from '../chart-helper';

interface WaterfallBar {
	x: number;
	y: number;
	width: number;
	height: number;
	fill: string;
	label: string;
	/** Pre-formatted tooltip body: signed delta (or total) + running level. */
	text: string;
}

interface Connector {
	x1: number;
	x2: number;
	y: number;
}

/**
 * Waterfall chart: each value is a DELTA from the previous running total; bars float at the
 * running level, colored by sign. Indices listed in `data.totals` are absolute totals/subtotals
 * instead — drawn from the zero baseline in a neutral color. Dashed connectors carry the running
 * level from each bar to the next, the classic P&L bridge reading.
 */
@Component({
	standalone:  false,
	selector:    '[cs-waterfall-chart]',
	templateUrl: './waterfall-chart.component.html'
})
export class CsWaterfallChartComponent implements OnInit, OnChanges {

	@Input() config: ChartConfig;

	// Semantic ink, not the categorical palette: a waterfall encodes meaning in color
	// (gain / loss / total) — styleguide accent green / accent red / primary blue.
	readonly upColor    = '#13ac3e';
	readonly downColor  = '#f53b3b';
	readonly totalColor = '#3884ff';

	maxValue: number;
	minValue = 0;
	yStep: number;
	xStep: number;
	yFactor: number;
	xOffsets       = 50;
	yOffsets       = 35;
	firstBarOffset = 10;
	rotateXLabels  = false;
	axisConfig;
	bars: WaterfallBar[] = [];
	connectors: Connector[] = [];

	private isDataReady() {
		return this.config?.data?.datasets?.length && this.config.data.datasets[0].data?.length;
	}

	ngOnInit() {
		setTimeout(() => {
			if (this.isDataReady()) { this.calculate(); }
		}, 1);
	}

	ngOnChanges(changes: any) {
		if (changes.hasOwnProperty('config') && this.isDataReady()) {
			this.calculate();
		}
	}

	/** Whether index i is an absolute total/subtotal bar rather than a delta. */
	private isTotal(i: number): boolean {
		return Array.isArray(this.config.data.totals) && this.config.data.totals.includes(i);
	}

	/**
	 * The plotted extents are the RUNNING levels, not the raw deltas: a chart of small
	 * deltas on a large base must still fit every intermediate cumulative value.
	 */
	private runningLevels(): number[] {
		const values = this.config.data.datasets[0].data;
		const levels: number[] = [];
		let running = 0;
		for (let i = 0; i < values.length; i++) {
			running = this.isTotal(i) ? values[i] : running + values[i];
			levels.push(running);
		}
		return levels;
	}

	private initScale() {
		const levels = this.runningLevels();
		const rawMax = Math.max(0, ...levels);
		const rawMin = Math.min(0, ...levels);
		if (rawMin < 0) {
			const magnitude = Math.max(Math.abs(rawMax), Math.abs(rawMin), 1);
			this.yStep    = ChartHelper.getMaxStepValue(magnitude, this.config.ySteps) / this.config.ySteps;
			this.maxValue = Math.ceil(rawMax / this.yStep) * this.yStep;
			this.minValue = Math.floor(rawMin / this.yStep) * this.yStep;
		} else {
			this.maxValue = ChartHelper.getMaxStepValue(rawMax, this.config.ySteps);
			this.minValue = 0;
			this.yStep    = this.maxValue / this.config.ySteps;
		}
		if (this.maxValue === this.minValue) { this.maxValue = this.minValue + 1; }

		const n = this.config.data.datasets[0].data.length;
		this.xStep         = (this.config.chartWidth - this.config.xPadding * 2 - this.xOffsets) / n;
		this.rotateXLabels = this.xStep < 90;
		this.yOffsets      = this.rotateXLabels ? 105 : 35;
		this.xStep         = (this.config.chartWidth - this.config.xPadding * 2 - this.xOffsets * 2) / n;
		this.yFactor       = (this.config.chartHeight - this.config.yPadding * 2 - this.yOffsets) / (this.maxValue - this.minValue);
	}

	private initAxisConfig() {
		this.axisConfig   = { chartWidth: this.config.chartWidth };
		this.axisConfig.s = {
			x: Math.round(this.config.xPadding + this.xOffsets) + 0.5,
			y: Math.round(this.config.yPadding - this.yOffsets) + 0.5
		};
		this.axisConfig.e = {
			x: Math.round(this.config.chartWidth - this.config.xPadding - this.xOffsets) + 0.5,
			y: Math.round(this.config.chartHeight - this.config.yPadding - this.yOffsets) + 0.5
		};
		this.axisConfig.ySteps = [];
		this.axisConfig.xSteps = [];
		const stepCount = Math.max(1, Math.round((this.maxValue - this.minValue) / this.yStep));
		for (let i = (this.minValue < 0 ? 0 : 1); i <= stepCount; i++) {
			const value = this.minValue + i * this.yStep;
			this.axisConfig.ySteps.push({
				text:   this.config.yLabelFormatter(ChartHelper.getValueFormatted(Math.round(value))),
				isZero: this.minValue < 0 && value === 0,
				y:      Math.round(this.getTranslatedY(value)) + 0.5
			});
		}
		const labels   = this.config.data.labels;
		const rotate   = this.rotateXLabels;
		const maxChars = rotate ? 13 : Math.max(4, Math.floor(this.xStep / 7));
		// One bar per category, centered in its slot (bar spans the middle half of xStep).
		for (let i = 0, l = labels.length; i < l; i++) {
			const full = labels[i] == null ? '' : String(labels[i]);
			this.axisConfig.xSteps.push({
				text:     full.length > maxChars ? full.slice(0, maxChars - 1).trimEnd() + '…' : full,
				fullText: full,
				rotate,
				x:        Math.round(this.config.xPadding + this.xOffsets + this.firstBarOffset + (i + 0.5) * this.xStep) + 0.5
			});
		}
	}

	private calculate() {
		if (!this.config.chartWidth || !this.config.chartHeight) { return; }
		this.initScale();
		this.initAxisConfig();

		const values   = this.config.data.datasets[0].data;
		const barWidth = Math.max(2, Math.round(this.xStep / 2));
		const bars: WaterfallBar[]      = [];
		const connectors: Connector[]   = [];
		let running = 0;

		for (let i = 0; i < values.length; i++) {
			const total = this.isTotal(i);
			const start = total ? 0 : running;
			const end   = total ? values[i] : running + values[i];
			const delta = end - start;
			running     = end;

			const x = Math.round(this.config.xPadding + this.xOffsets + this.firstBarOffset
				+ (i + 0.5) * this.xStep - barWidth / 2);
			const yTop = this.getTranslatedY(Math.max(start, end));
			const fill = total ? this.totalColor : (delta >= 0 ? this.upColor : this.downColor);
			const signed = (total ? '' : (delta >= 0 ? '+' : '')) + this.format(total ? end : delta);

			bars.push({
				x,
				y:      Math.round(yTop),
				width:  barWidth,
				height: Math.max(1, Math.round(Math.abs(delta) * this.yFactor)),
				fill,
				label:  this.config.data.labels?.[i] ?? '',
				text:   total ? signed : `${signed}\nrunning: ${this.format(end)}`
			});

			// Connector: carries this bar's END level across the gap to the next bar.
			if (i < values.length - 1) {
				const y = Math.round(this.getTranslatedY(end)) + 0.5;
				connectors.push({ x1: x + barWidth, x2: x + barWidth + (this.xStep - barWidth), y });
			}
		}
		this.bars       = bars;
		this.connectors = connectors;
	}

	private format(value: number): string {
		const rounded = Math.round(value * 100) / 100;
		return ChartHelper.getValueLocale(rounded);
	}

	private getTranslatedY(value: number) {
		return this.config.chartHeight - this.config.yPadding - this.yOffsets - (value - this.minValue) * this.yFactor;
	}
}
